// Jihozlar (inventar) — `xsib-platform` `inventory` moduli bilan kontrakt.
//
//   GET    /api/inventory/equipment?q&type&state&ownerType&ownerId&siteId&page&size
//                                              → {rows, total, page, size}
//   GET    /api/inventory/equipment/{id}       → Equipment
//   POST   /api/inventory/equipment            → Equipment
//   PUT    /api/inventory/equipment/{id}       → Equipment
//   POST   /api/inventory/equipment/{id}/assign      {ownerType, ownerId}
//   POST   /api/inventory/equipment/{id}/unassign
//   POST   /api/inventory/equipment/{id}/mark-faulty {reason}
//   POST   /api/inventory/equipment/{id}/repair
//   POST   /api/inventory/equipment/{id}/mark-lost   {reason}
//   POST   /api/inventory/equipment/{id}/found
//   DELETE /api/inventory/equipment/{id}       — HISOBDAN CHIQARISH
//
// Huquqlar: `inventory.equipment.read` / `inventory.equipment.write`.
//
// ⚠️ `DELETE` — O'CHIRISH EMAS, HISOBDAN CHIQARISH (`DECOMMISSIONED`). Yozuv
// bazada qoladi: jihozning tarixi (kimga berilgan, qachon buzilgan) inventar
// hisobining o'zi. Panelda shuning uchun tugma "o'chirish" deb emas,
// "hisobdan chiqarish" deb ataladi va oqibati matn bilan aytiladi — aks holda
// operator uni "xato kiritdim, tozalayman" deb bosardi.
//
// ⚠️ HOLAT MAYDONI SERVERNIKI. `state` mijoz tomondan yuborilmaydi: u
// harakatlarning natijasi (biriktirildi → `ASSIGNED`, buzildi → `FAULTY`).
// Uni to'g'ridan-to'g'ri tahrirlash imkoniyati holat mashinasini chetlab
// o'tishga yo'l ochardi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { type STATE_ALL, type STATE_OPEN_ONLY, showsClosed } from '@/shared/ui/table/closedFilter'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

export const EQUIPMENT_TYPES = ['ROUTER', 'ONU', 'MODEM', 'CABLE', 'OTHER'] as const
export type EquipmentType = (typeof EQUIPMENT_TYPES)[number]

/**
 * ⚠️ `LOST` ro'yxatda BOR va u 2026-08-13 da QO'SHILDI — nuqson tuzatildi.
 *
 * Backend `EquipmentState` enum'ida beshta qiymat bor (`LOST` — "yo'qolgan",
 * `FAULTY` dan ATAYLAB ajratilgan: nosoz jihoz omborda turibdi va
 * ta'mirlanishi mumkin, yo'qolgani esa fizik jihatdan yo'q). Bu yerda esa
 * to'rttasi turardi, ya'ni `z.enum(EQUIPMENT_STATES)` yo'qolgan deb
 * belgilangan BITTA jihoz javobga tushishi bilan BUTUN sahifani yiqitardi —
 * jadval umuman chizilmasdi va sabab konsoldagi zod xatosida qolardi.
 */
export const EQUIPMENT_STATES = [
  'IN_STOCK',
  'ASSIGNED',
  'FAULTY',
  'LOST',
  'DECOMMISSIONED',
] as const
export type EquipmentState = (typeof EQUIPMENT_STATES)[number]

/**
 * «Yo'qolgan deb belgilash» QAYSI holatlardan o'tadi.
 *
 * ⚠️ Bu ro'yxat panelning o'z qarori EMAS — u backenddagi holat mashinasidan
 * ko'chirilgan (`EquipmentState.canTransitionTo` dagi `LOST` ustuni va aynan
 * o'sha qoidani majburlaydigan `equipment_state_transition_guard` triggeri).
 * Ya'ni: omborda turgan, kimgadir berilgan va nosoz jihoz yo'qolishi mumkin;
 * hisobdan chiqarilgani esa YAKUNIY holat va uni qo'zg'atib bo'lmaydi.
 *
 * ⚠️ Nusxa bo'lgani uchun u ajralib ketishi mumkin va aynan shuning uchun
 * `tests/tester-bugs.mjs` uni Java manbasi bilan solishtiradi. Ajralib
 * ketganda oqibat jimgina bo'lardi: operator bosadigan tugma ko'rinadi,
 * server esa 409 (`ILLEGAL_TRANSITION`) qaytaradi — ya'ni panel bilmagan
 * narsani bilgandek ko'rsatgan bo'lardi.
 */
export const CAN_MARK_LOST = ['IN_STOCK', 'ASSIGNED', 'FAULTY'] as const

/**
 * «Topilgan deb belgilash» — FAQAT yo'qolgan jihozda.
 *
 * ⚠️ Bu enum'dan emas, `EquipmentServiceImpl.found` dan olingan: `LOST ->
 * IN_STOCK` o'tishini holat mashinasi ruxsat etadi, lekin servis qo'shimcha
 * ravishda joriy holat AYNAN `LOST` ekanini talab qiladi. Sabab o'sha yerda
 * yozilgan: `FAULTY -> IN_STOCK` ham bor, lekin u BOSHQA amal (`repair`) va
 * audit jurnalida ham boshqacha yoziladi — aks holda topilgan jihoz
 * «ta'mirlandi» bo'lib qayd etilardi.
 */
export const CAN_MARK_FOUND = ['LOST'] as const

const equipmentSchema = z.object({
  id: uuidSchema,
  /** ⚠️ Javobda bor, lekin panel ishlatmaydi: izolyatsiya chegarasi, ma'lumot emas */
  tenantId: uuidSchema.nullish(),
  type: z.enum(EQUIPMENT_TYPES),
  model: z.string().nullish(),
  serialNumber: z.string(),
  state: z.enum(EQUIPMENT_STATES),
  /**
   * ⚠️ `ownerType`/`ownerId` — ERKIN SATR, enum EMAS. Egasi abonent ham,
   * ob'ekt ham, xodim ham bo'lishi mumkin va bu ro'yxat platform tashqarisida
   * kengayadi. Shuning uchun panel uni TANIMAGAN holda ham ko'rsata olishi
   * kerak — noma'lum qiymat shunchaki o'z holicha chiziladi.
   */
  ownerType: z.string().nullish(),
  ownerId: z.string().nullish(),
  assignedAt: instantSchema.nullish(),
  notes: z.string().nullish(),
  siteId: uuidSchema.nullish(),
  /**
   * ⚠️ QAYSI OMBOR (tashkiliy tugun) hisobida — `siteId` BILAN
   * CHALKASHTIRMANG. `siteId` FIZIK joy ("Chorsu binosi"), bu esa TASHKILIY
   * birlik ("Mazar ombori"): bitta binoda ikki filialning ombori turishi
   * mumkin va aksincha (`Equipment` javadoc'i, `V202608131400`).
   *
   * ⚠️ 2026-08-13 gacha bu maydon sxemada UMUMAN YO'Q edi va oqibati
   * jimgina og'ir edi: `PUT` tanasi `nodeId` siz ketardi, Jackson uni
   * `null` qilardi va to'liq almashtirish qoidasi bilan ombor O'CHARDI.
   * `node_id IS NULL` jihoz esa `SUBTREE` doirali foydalanuvchiga umuman
   * ko'rinmaydi — ya'ni oddiy tahrir jihozni filial boshlig'idan yashirib
   * qo'yardi (`V202608131400` §3.1).
   */
  nodeId: uuidSchema.nullish(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

const equipmentPageSchema = z.object({
  rows: z.array(equipmentSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface Equipment {
  id: string
  type: EquipmentType
  model: string | null
  serialNumber: string
  state: EquipmentState
  ownerType: string | null
  ownerId: string | null
  assignedAt: string | null
  notes: string | null
  siteId: string | null
  /** Qaysi ombor hisobida — sxemadagi izohga qarang */
  nodeId: string | null
  createdAt: string | null
  updatedAt: string | null
}

function toEquipment(raw: z.infer<typeof equipmentSchema>): Equipment {
  return {
    id: raw.id,
    type: raw.type,
    model: raw.model ?? null,
    serialNumber: raw.serialNumber,
    state: raw.state,
    ownerType: raw.ownerType ?? null,
    ownerId: raw.ownerId ?? null,
    assignedAt: raw.assignedAt ?? null,
    notes: raw.notes ?? null,
    siteId: raw.siteId ?? null,
    nodeId: raw.nodeId ?? null,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  }
}

/**
 * Jihoz uchun «yopiq» — FAQAT `DECOMMISSIONED` (`EquipmentState.isClosed()`).
 *
 * ⚠️ `FAULTY` va `LOST` ro'yxatda QOLADI va bu ataylab: nosoz jihoz
 * ta'mirlanishi kerak, yo'qolgani esa qidirilishi yoki hisobdan
 * chiqarilishi kerak — ikkalasi ham OCHIQ ish. Ularni yashirish
 * «ta'mirlash uchun nechta jihoz bor» degan savolni javobsiz
 * qoldirardi.
 */
export const EQUIPMENT_CLOSED_STATE: EquipmentState = 'DECOMMISSIONED'

/**
 * Holat tanlovi: aniq holat YOKI ikki maxsus qiymatdan biri
 * ({@code STATE_OPEN_ONLY} — sukut, {@code STATE_ALL} — hammasi).
 */
export type EquipmentStateFilter =
  | EquipmentState
  | typeof STATE_OPEN_ONLY
  | typeof STATE_ALL

function isEquipmentState(value: string): value is EquipmentState {
  return (EQUIPMENT_STATES as readonly string[]).includes(value)
}

export interface EquipmentFilter {
  type: EquipmentType | null
  state: EquipmentStateFilter
  siteId: string | null
}

/**
 * So'rov parametrlari — FUNKSIYA, chunki qorovul uni CHAQIRIB o'lchaydi.
 *
 * ⚠️ `includeClosed` DOIM yuboriladi: serverdagi sukut qiymat `true`
 * (`EquipmentResource#search`), ya'ni parametrsiz so'rov hisobdan
 * chiqarilganlarni ham qaytaradi.
 *
 * ⚠️ `state` aniq berilganda server bayroqni E'TIBORSIZ qoldiradi
 * (`EquipmentQuery.excludesClosed`) — usiz «faqat hisobdan
 * chiqarilganlarni ko'rsat» DOIM bo'sh natija berardi.
 */
export function equipmentListParams(
  filter: EquipmentFilter,
  table: TableState,
): Record<string, unknown> {
  const search = table.search.trim()
  return {
    q: search === '' ? undefined : search,
    ...(filter.type ? { type: filter.type } : {}),
    ...(isEquipmentState(filter.state) ? { state: filter.state } : {}),
    ...(filter.siteId ? { siteId: filter.siteId } : {}),
    includeClosed: showsClosed(filter.state),
    // Backend sahifani 0 dan sanaydi — `/api/nas` va `/api/sites` bilan bir xil
    page: table.page - 1,
    size: table.pageSize,
  }
}

export function fetchEquipmentPage(
  filter: EquipmentFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<Equipment>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/inventory/equipment', {
      signal,
      params: equipmentListParams(filter, state),
    })
    const parsed = equipmentPageSchema.parse(response.data)
    return { items: parsed.rows.map(toEquipment), total: parsed.total }
  }
}

/**
 * Sukut ko'rinish NECHTA jihozni yashirganini sanaydi.
 *
 * ⚠️ Holat AYNAN `DECOMMISSIONED` ga qo'yiladi — server shu holatda
 * bayroqni e'tiborsiz qoldiradi va qatorlarni ROSTDAN qaytaradi.
 */
export function equipmentClosedCountParams(
  filter: EquipmentFilter,
  table: TableState,
): Record<string, unknown> {
  return {
    ...equipmentListParams({ ...filter, state: EQUIPMENT_CLOSED_STATE }, table),
    page: 0,
    size: 1,
  }
}

export function countClosedEquipment(
  filter: EquipmentFilter,
): (state: TableState, signal: AbortSignal) => Promise<number> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/inventory/equipment', {
      signal,
      params: equipmentClosedCountParams(filter, state),
    })
    return equipmentPageSchema.parse(response.data).total
  }
}

export async function fetchEquipment(id: string): Promise<Equipment> {
  const response = await api.get<unknown>(`/api/inventory/equipment/${id}`)
  return toEquipment(equipmentSchema.parse(response.data))
}

/**
 * Yaratish.
 *
 * ⚠️ `serialNumber` FAQAT shu yerda beriladi va keyin O'ZGARMAYDI
 * ({@link EquipmentUpdate} da u yo'q). Sabab: seriya raqami jihozning
 * o'zligi — uni tahrirlash "boshqa qurilma" degani bo'lardi va tarix
 * (kimga berilgan, qachon buzilgan) noto'g'ri qurilmaga bog'lanib qolardi.
 */
export interface EquipmentDraft {
  type: EquipmentType
  model?: string | null
  serialNumber: string
  notes?: string | null
  siteId?: string | null
  /**
   * ⚠️ TOR DOIRA UCHUN AMALDA MAJBURIY, garchi tipda ixtiyoriy bo'lsa ham.
   * `EquipmentServiceImpl#register` seriya tekshiruvidan OLDIN
   * `requireTargetNodeInScope(draft.nodeId())` ni chaqiradi va `SUBTREE`
   * doirada `null` tugun doiradan o'tmaydi — ya'ni omborsiz yaratilgan
   * jihoz uni yaratgan odamga ham ko'rinmay qolardi. `TENANT`/`GLOBAL`
   * doirada esa `null` odatiy hol, shuning uchun tip ixtiyoriy.
   */
  nodeId?: string | null
}

/** Tahrirlash — model, izoh, joylashuv va ombor (`PUT` tanasi shu to'rttasi) */
export interface EquipmentUpdate {
  model?: string | null
  notes?: string | null
  siteId?: string | null
  /**
   * ⚠️ `siteId` BILAN BIR XIL SEMANTIKADA EMAS. `siteId` to'liq
   * almashtiriladi (`null` = tozala), `nodeId` esa
   * `COALESCE(:nodeId, node_id)` — `null` = "TEGMA"
   * (`EquipmentRepository#updateDetails`, 2026-08-13). Ya'ni bu endpoint
   * orqali omborni BO'SHATIB bo'lmaydi, faqat boshqasiga ko'chirish
   * mumkin — panel shuning uchun tahrirlashda tozalash tugmasini
   * chizmaydi.
   */
  nodeId?: string | null
}

export async function createEquipment(draft: EquipmentDraft): Promise<Equipment> {
  const response = await api.post<unknown>('/api/inventory/equipment', draft)
  return toEquipment(equipmentSchema.parse(response.data))
}

export async function updateEquipment(id: string, draft: EquipmentUpdate): Promise<Equipment> {
  const response = await api.put<unknown>(`/api/inventory/equipment/${id}`, draft)
  return toEquipment(equipmentSchema.parse(response.data))
}

// --- Hayotiy sikl amallari --------------------------------------------------

/**
 * Amal natijasini `OperationResult` ga keltiradi.
 *
 * ⚠️ NEGA `parse` EMAS, `safeParse`. Sayt holatidan (`/api/sites/{id}/state`)
 * farqli o'laroq, bu endpointlarning javob SHAKLI kontraktda qat'iy
 * belgilanmagan — ular `Equipment` ni ham, `OperationResult` ni ham
 * qaytarishi mumkin. Qat'iy tekshiruv shu holatda MUVAFFAQIYATLI amalni
 * ekranda XATO qilib ko'rsatardi (server ishini bajardi, panel esa yiqildi).
 *
 * Teskarisi ham to'g'ri emas: agar server chindan `OperationResult` yuborsa
 * va u `FAILED`/`PARTIAL` bo'lsa, uni jimgina "bajarildi" deb ko'rsatish
 * yolg'on bo'lardi. Shuning uchun `null` — "server holat aytmadi, HTTP 2xx
 * va envelope `success` ga ishonamiz", chaqiruvchi esa o'z xabarini
 * ko'rsatadi.
 */
function toResult(body: unknown): OperationResult | null {
  const parsed = operationResultSchema.safeParse(body)
  return parsed.success ? parsed.data : null
}

/**
 * Jihozni egasiga biriktirish.
 *
 * ⚠️ ESKI IZOH BEKOR QILINDI (2026-08-13, XSB-003). Bu yerda "panelda bu amal
 * uchun tugma yo'q" deb yozilgan edi va sabab shu edi: `ownerId` — boshqa
 * moduldagi yozuvning identifikatori, uni tanlash uchun alohida tanlagich
 * kerak, "id ni qo'lda yozing" maydoni esa yaroqsiz biriktirishlarni ochib
 * berardi.
 *
 * Dalil hamon TO'G'RI — xom UUID maydoni chindan ham yaroqsiz. Faqat undan
 * chiqarilgan xulosa o'zgardi: tanlagichni YOZMASLIK o'rniga u YOZILDI
 * (`ownerApi.ts` + `EquipmentOwnerModal.tsx`). Tashqi tekshiruvchi (XSB-003)
 * aynan shuni topdi: kartochkada "Egasi: tayinlanmagan" ko'rinardi, lekin
 * uni tayinlaydigan joy yo'q edi.
 *
 * ⚠️ `ownerType` — ERKIN SATR EMAS. Backend `OwnerType` enum'ini kutadi
 * (`SUBSCRIBER`/`AGENT`/`VENUE`/`OTHER`), noma'lum qiymat 400 bilan qaytadi.
 */
export async function assignEquipment(
  id: string,
  ownerType: string,
  ownerId: string,
): Promise<OperationResult | null> {
  const response = await api.post<unknown>(`/api/inventory/equipment/${id}/assign`, {
    ownerType,
    ownerId,
  })
  return toResult(response.data)
}

export async function unassignEquipment(id: string): Promise<OperationResult | null> {
  const response = await api.post<unknown>(`/api/inventory/equipment/${id}/unassign`, {})
  return toResult(response.data)
}

export async function markEquipmentFaulty(
  id: string,
  reason: string,
): Promise<OperationResult | null> {
  const response = await api.post<unknown>(`/api/inventory/equipment/${id}/mark-faulty`, { reason })
  return toResult(response.data)
}

export async function repairEquipment(id: string): Promise<OperationResult | null> {
  const response = await api.post<unknown>(`/api/inventory/equipment/${id}/repair`, {})
  return toResult(response.data)
}

/**
 * Yo'qolgan deb belgilash (`LOST`).
 *
 * ⚠️ `markEquipmentFaulty` BILAN BIR XIL EMAS va ular ataylab ajratilgan
 * (`EquipmentState.LOST` javadoc'i): nosoz jihoz omborda TURIBDI va
 * ta'mirlanishi mumkin, yo'qolgani esa jismonan yo'q. Bittaga qo'shilsa
 * «ta'mirlash uchun nechta jihoz bor» degan savolga javob berib bo'lmasdi.
 *
 * ⚠️ EGA TOZALANADI. Biriktirilgan jihoz yo'qolgan deb belgilansa server uni
 * egasidan ham yechadi (`transition` da `owner_*` faqat `ASSIGNED` da
 * to'ldiriladi), kimda bo'lgani esa audit yozuvida (`wasOwnerType`,
 * `wasOwnerId`) qoladi. Shuning uchun oynada bu OLDINDAN aytiladi.
 *
 * ⚠️ `reason` server uchun IXTIYORIY (`ReasonBody` bo'sh ham bo'lishi mumkin),
 * lekin panel uni MAJBURIY qiladi — `markEquipmentFaulty` bilan bir xil
 * mulohaza: sababsiz «yo'qoldi» yozuvi keyin hech kimga hech narsa aytmaydi.
 * Bu panelning qo'shimcha qoidasi, serverning qoidasi emas.
 */
export async function markEquipmentLost(
  id: string,
  reason: string,
): Promise<OperationResult | null> {
  const response = await api.post<unknown>(`/api/inventory/equipment/${id}/mark-lost`, { reason })
  return toResult(response.data)
}

/**
 * Yo'qolgan jihoz topildi (`LOST -> IN_STOCK`).
 *
 * ⚠️ Bu amal KERAK va uni «qaytadan ro'yxatga olish» bilan almashtirib
 * bo'lmaydi: seriya raqami tenant ichida unikal
 * (`equipment_tenant_serial_uq`), ya'ni topilgan jihozni yangidan kiritish
 * IMKONSIZ. Shu sababdan `LOST` yakuniy holat emas.
 */
export async function foundEquipment(id: string): Promise<OperationResult | null> {
  const response = await api.post<unknown>(`/api/inventory/equipment/${id}/found`, {})
  return toResult(response.data)
}

/** Hisobdan chiqarish (`DECOMMISSIONED`) — yozuv qoladi, fayl sarlavhasiga qarang */
export async function decommissionEquipment(id: string): Promise<OperationResult | null> {
  const response = await api.delete<unknown>(`/api/inventory/equipment/${id}`)
  return toResult(response.data)
}
